import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { useParallax } from 'react-scroll-parallax';
import { Link } from 'react-router-dom';
import './ServicePages.css';

const GenerativeAI = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const headerParallax = useParallax({
    translateY: [0, 50],
    opacity: [1, 0.5],
    scale: [1, 0.9],
    easing: 'easeInQuad',
    startScroll: 0,
    endScroll: 600
  });

  // Animation variants for staggered animations
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: 'spring',
        stiffness: 100,
        damping: 12
      }
    }
  };

  const cardVariants = {
    hidden: { y: 50, opacity: 0, scale: 0.9 },
    visible: {
      y: 0,
      opacity: 1,
      scale: 1,
      transition: {
        type: 'spring',
        stiffness: 100,
        damping: 12
      }
    },
    hover: {
      scale: 1.05,
      boxShadow: "0px 10px 30px rgba(0, 0, 0, 0.3)",
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 15
      }
    }
  };

  const scaleVariants = {
    hover: {
      scale: 1.05,
      boxShadow: "0px 10px 30px rgba(0, 0, 0, 0.2)",
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 15
      }
    }
  };

  return (
    <div className="service-page">
      {/* Hero Section */}
      <section className="service-hero generative-ai-hero" ref={headerParallax.ref}>
        <div className="service-hero-overlay"></div>
        <div className="service-hero-content">
          <h1>Generative AI</h1>
          <p>Transforming content creation and creative processes with artificial intelligence</p>
        </div>
      </section>

      {/* Overview Section */}
      <motion.section 
        className="service-overview"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
        variants={containerVariants}
      >
        <motion.h2 variants={itemVariants}>What is Generative AI?</motion.h2>
        <motion.p variants={itemVariants}>
          Generative AI refers to artificial intelligence systems that can create new content, including text, images, audio, and more. 
          These systems learn patterns from existing data and generate new outputs that reflect those patterns while adding novel elements.
          At Hyperion AI, we leverage state-of-the-art generative models to help businesses create content, design products, and innovate faster.
        </motion.p>
      </motion.section>

      {/* Use Cases Section */}
      <motion.section 
        className="service-use-cases"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
        variants={containerVariants}
      >
        <motion.h2 variants={itemVariants}>Use Cases</motion.h2>
        <motion.div 
          className="use-cases-grid"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
        >
          <motion.div 
            className="use-case-card generative-ai-card-1"
            variants={cardVariants}
            whileHover="hover"
          >
            <div className="use-case-icon">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3>Retrieval-Augmented Generation</h3>
            <p>AI systems that combine knowledge retrieval with generative capabilities to provide accurate, contextual, and up-to-date responses.</p>
          </motion.div>
          
          <motion.div 
            className="use-case-card generative-ai-card-2"
            variants={cardVariants}
            whileHover="hover"
          >
            <div className="use-case-icon">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <h3>Image & Design Generation</h3>
            <p>Creation of custom artwork, product visualizations, and design concepts based on text prompts.</p>
          </motion.div>
          
          <motion.div 
            className="use-case-card generative-ai-card-3"
            variants={cardVariants}
            whileHover="hover"
          >
            <div className="use-case-icon">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
            <h3>Agentic AI Workflows</h3>
            <p>Autonomous AI agents that can plan, execute tasks, and solve complex problems by breaking them down into manageable steps.</p>
          </motion.div>
          
          <motion.div 
            className="use-case-card generative-ai-card-4"
            variants={cardVariants}
            whileHover="hover"
          >
            <div className="use-case-icon">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
              </svg>
            </div>
            <h3>E-commerce Personalization</h3>
            <p>AI-powered product recommendations, dynamic pricing, and personalized shopping experiences that increase conversion rates.</p>
          </motion.div>
        </motion.div>
      </motion.section>

      {/* Technology Section */}
      <motion.section 
        className="service-technology"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
        variants={containerVariants}
      >
        <motion.h2 variants={itemVariants}>Our Technology</motion.h2>
        <motion.p variants={itemVariants}>
          Hyperion AI leverages state-of-the-art generative models to deliver creative, accurate, and contextually relevant content that meets your specific business needs.
        </motion.p>
        <motion.div className="technology-features" variants={containerVariants}>
          <motion.div className="technology-feature" variants={itemVariants}>
            <h3>Large Language Models</h3>
            <p>Advanced transformer-based models fine-tuned for specific domains and use cases.</p>
          </motion.div>
          <motion.div className="technology-feature" variants={itemVariants}>
            <h3>Diffusion Models</h3>
            <p>Cutting-edge image generation systems that create high-quality visuals from text descriptions.</p>
          </motion.div>
          <motion.div className="technology-feature" variants={itemVariants}>
            <h3>Multimodal Systems</h3>
            <p>Integrated solutions that combine text, image, and audio generation for comprehensive content creation.</p>
          </motion.div>
        </motion.div>
      </motion.section>

      {/* CTA Section */}
      <section className="service-cta">
        <div className="service-cta-content">
          <h2>Ready to Harness the Power of Generative AI?</h2>
          <p>Contact our team to discuss how our generative AI solutions can drive innovation in your business.</p>
          <Link to="/contact-us" className="cta-button">Get Started</Link>
        </div>
      </section>
    </div>
  );
};

export default GenerativeAI;
